import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  bomTemplatesAdminApi,
  type RankedTemplate,
  type BomTemplateDetail,
  type CreateBomTemplateBody,
  type UpdateBomTemplateBody,
  type BomTemplateUsage,
} from "@/lib/bom-templates";
import { TemplateLineItemsEditor } from "@/components/bom/TemplateLineItemsEditor";
import {
  parseTemplateLineItems,
  serializeTemplateLineItems,
  type TemplateLineItem,
} from "@/lib/template-line-items";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { DetailModal } from "@/components/detail-modal";
import { EntityForm } from "@/components/entity-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IntentButton } from "@/components/intent-button";
import { toast } from "@/hooks/use-toast";

// Header fields are RHF-managed. Line items are edited in the
// TemplateLineItemsEditor modal (page state) and folded into the body on save.
const templateSchema = z.object({
  bomName: z.string().min(1, "Name is required").max(200),
  bomDesc: z.string().nullable(),
  indentBom: z.boolean(),
  promoBom: z.boolean(),
  sheetBom: z.boolean(),
  currencyCode: z.string(),
  markupPct: z.number(),
  quantities: z.string().nullable(),
  overs: z.string().nullable(),
});

type TemplateForm = z.infer<typeof templateSchema>;

const BLANK_FORM: TemplateForm = {
  bomName: "",
  bomDesc: null,
  indentBom: false,
  promoBom: false,
  sheetBom: false,
  currencyCode: "AUD",
  markupPct: 0,
  quantities: null,
  overs: null,
};

const ADMIN_LIST_KEY = ["bom-templates", "admin"] as const;

// Grid page size — passed to BOTH the fetch and the DataGrid so the two can't
// drift (the API defaults to 100, which would mislabel pages on a 50/page grid).
const PAGE_SIZE = 50;

const asNumberOrZero = (v: string) => (v === "" ? 0 : Number(v));
const asStringOrNull = (v: string) => (v === "" ? null : v);

export function TemplatesAdminPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [lineItems, setLineItems] = useState<TemplateLineItem[]>([]);
  const [lineItemsOpen, setLineItemsOpen] = useState(false);

  // List (0-based paging — first page page=0)
  const { data, isLoading } = useQuery({
    queryKey: [...ADMIN_LIST_KEY, page, q],
    queryFn: () => bomTemplatesAdminApi.listAdmin(q, page, PAGE_SIZE),
  });

  // List rows are RankedTemplate (no header fields, no rowVersion, no
  // lineItemsJson) → fetch the full detail when editing to populate the form,
  // resolve the rowVersion token, and seed the line-items editor.
  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: [...ADMIN_LIST_KEY, "detail", editingId],
    queryFn: () => bomTemplatesAdminApi.get(editingId!),
    enabled: editingId != null,
  });

  // Read-only "Used by" — resolved product/category names consuming this template.
  const usage = useQuery({
    queryKey: [...ADMIN_LIST_KEY, "usage", editingId],
    queryFn: () => bomTemplatesAdminApi.usage(editingId!),
    enabled: editingId != null,
  });

  // Seed line items from the fetched detail (or empty when creating).
  useEffect(() => {
    if (creating) {
      setLineItems([]);
    } else if (detail) {
      setLineItems(parseTemplateLineItems(detail.lineItemsJson));
    }
  }, [detail, creating]);

  // Create / update mutation
  const upsert = useMutation({
    mutationFn: (form: TemplateForm) => {
      if (editingId != null) {
        const body: UpdateBomTemplateBody = {
          bomName: form.bomName,
          bomDesc: form.bomDesc ?? null,
          indentBom: form.indentBom,
          promoBom: form.promoBom,
          sheetBom: form.sheetBom,
          currencyCode: form.currencyCode,
          markupPct: form.markupPct,
          quantities: form.quantities ?? null,
          overs: form.overs ?? null,
          lineItemsJson: serializeTemplateLineItems(lineItems),
          clearBomDesc: (form.bomDesc ?? null) == null,
          clearQuantities: (form.quantities ?? null) == null,
          clearOvers: (form.overs ?? null) == null,
        };
        return bomTemplatesAdminApi.update(editingId, body, detail?.rowVersion ?? null);
      }
      const body: CreateBomTemplateBody = {
        bomName: form.bomName,
        bomDesc: form.bomDesc ?? null,
        indentBom: form.indentBom,
        promoBom: form.promoBom,
        sheetBom: form.sheetBom,
        currencyCode: form.currencyCode,
        markupPct: form.markupPct,
        quantities: form.quantities ?? null,
        overs: form.overs ?? null,
        lineItemsJson: lineItems.length > 0 ? serializeTemplateLineItems(lineItems) : null,
      };
      return bomTemplatesAdminApi.create(body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_LIST_KEY });
      setCreating(false);
      setEditingId(null);
    },
    onError: (err: Error) => {
      toast.error(`Save failed: ${err.message}`);
    },
  });

  // Duplicate mutation
  const duplicate = useMutation({
    mutationFn: (id: number) => bomTemplatesAdminApi.duplicate(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_LIST_KEY });
      toast.success("Template duplicated");
    },
    onError: (err: Error) => {
      toast.error(`Duplicate failed: ${err.message}`);
    },
  });

  // Delete mutation — list rows lack the rowVersion, so fetch detail to resolve
  // the token before deleting (under InMemory rowVersion is null → empty token).
  const remove = useMutation({
    mutationFn: async (id: number) => {
      const d = await bomTemplatesAdminApi.get(id);
      return bomTemplatesAdminApi.remove(id, d.rowVersion);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_LIST_KEY });
    },
    onError: (err: Error) => {
      toast.error(`Cannot delete: ${err.message}`);
    },
  });

  // Columns
  const cols: DataGridColumn<RankedTemplate>[] = [
    { id: "bomName", header: "Name", sortable: true, accessor: (r) => r.bomName },
    { id: "bomDesc", header: "Description", accessor: (r) => r.bomDesc ?? "—" },
    {
      id: "actions",
      header: "",
      accessor: (r) => (
        <div className="flex gap-1">
          <IntentButton intent="ghost" onClick={() => setEditingId(r.bomTemplateId)}>
            Edit
          </IntentButton>
          <IntentButton intent="ghost" onClick={() => duplicate.mutate(r.bomTemplateId)}>
            Duplicate
          </IntentButton>
          <IntentButton intent="ghost" onClick={() => remove.mutate(r.bomTemplateId)}>
            Delete
          </IntentButton>
        </div>
      ),
    },
  ];

  // Default form values when editing — header fields only (from fetched detail).
  function toFormValues(d: BomTemplateDetail): TemplateForm {
    return {
      bomName: d.bomName,
      bomDesc: d.bomDesc,
      indentBom: d.indentBom,
      promoBom: d.promoBom,
      sheetBom: d.sheetBom,
      currencyCode: d.currencyCode,
      markupPct: d.markupPct,
      quantities: d.quantities,
      overs: d.overs,
    };
  }

  const drawerOpen = creating || editingId !== null;
  const drawerTitle = editingId != null ? "Edit BOM template" : "New BOM template";

  // When editing, only mount the form once the detail has loaded so RHF's
  // defaultValues capture the fetched values (defaultValues are read once on
  // mount). Keying the form on editingId remounts it per row.
  const showForm = creating || (editingId != null && detail != null);

  return (
    <ListPage backTo="/admin"
      title="BOM templates"
      primaryAction={<Button onClick={() => setCreating(true)}><Plus className="mr-2 h-4 w-4" /> Add Template</Button>}
      search={{
        value: q,
        onChange: (v) => {
          setQ(v);
          setPage(0);
        },
        placeholder: "Search name…",
      }}
    >
      <DataGrid
        rows={data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.bomTemplateId}
        totalRows={data?.total ?? 0}
        pageIndex={page}
        pageSize={PAGE_SIZE}
        loading={isLoading}
        onPageChange={setPage}
      />

      <DetailModal
        open={drawerOpen}
        onOpenChange={(o) => {
          if (!o) {
            setCreating(false);
            setEditingId(null);
          }
        }}
        title={drawerTitle}
      >
        {editingId != null && detailLoading && !detail ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : showForm ? (
          <EntityForm
            key={editingId ?? "new"}
            schema={templateSchema}
            defaultValues={editingId != null && detail ? toFormValues(detail) : BLANK_FORM}
            onSubmit={(v) => upsert.mutate(v)}
          >
            {(form) => (
              <div className="space-y-3">
                {/* Line items — open the structured editor */}
                {editingId != null && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setLineItemsOpen(true)}
                  >
                    Edit line items ({lineItems.length})
                  </Button>
                )}

                {/* Used by — read-only resolved product/category names */}
                {editingId != null && (
                  <div className="space-y-1">
                    <h3 className="text-sm font-semibold">Used by</h3>
                    {usage.data && usage.data.length > 0 ? (
                      <ul className="list-disc pl-5 text-sm">
                        {usage.data.map((u: BomTemplateUsage) => (
                          <li key={`${u.productId}`}>
                            {u.productName ?? `Product #${u.productId}`}
                            {u.categoryName ? ` · ${u.categoryName}` : ""}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-muted-foreground">Not used by any product yet.</p>
                    )}
                  </div>
                )}

                {/* Name */}
                <div className="space-y-1">
                  <Label htmlFor="bt-name">Name</Label>
                  <Input id="bt-name" {...form.register("bomName")} />
                </div>

                {/* Description */}
                <div className="space-y-1">
                  <Label htmlFor="bt-desc">Description</Label>
                  <Input id="bt-desc" {...form.register("bomDesc", { setValueAs: asStringOrNull })} />
                </div>

                {/* Flags */}
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <input id="bt-sheet" type="checkbox" {...form.register("sheetBom")} />
                    <Label htmlFor="bt-sheet">Sheet</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input id="bt-promo" type="checkbox" {...form.register("promoBom")} />
                    <Label htmlFor="bt-promo">Promo</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input id="bt-indent" type="checkbox" {...form.register("indentBom")} />
                    <Label htmlFor="bt-indent">Indent</Label>
                  </div>
                </div>

                {/* Currency */}
                <div className="space-y-1">
                  <Label htmlFor="bt-currency">Currency</Label>
                  <Input id="bt-currency" {...form.register("currencyCode")} />
                </div>

                {/* Markup % */}
                <div className="space-y-1">
                  <Label htmlFor="bt-markup">Markup %</Label>
                  <Input
                    id="bt-markup"
                    type="number"
                    step="any"
                    {...form.register("markupPct", { setValueAs: asNumberOrZero })}
                  />
                </div>

                {/* Quantities (CSV) */}
                <div className="space-y-1">
                  <Label htmlFor="bt-quantities">Quantities (CSV)</Label>
                  <Input
                    id="bt-quantities"
                    {...form.register("quantities", { setValueAs: asStringOrNull })}
                  />
                </div>

                {/* Overs (CSV) */}
                <div className="space-y-1">
                  <Label htmlFor="bt-overs">Overs (CSV)</Label>
                  <Input id="bt-overs" {...form.register("overs", { setValueAs: asStringOrNull })} />
                </div>

                <Button type="submit">Save</Button>
              </div>
            )}
          </EntityForm>
        ) : null}
      </DetailModal>

      <TemplateLineItemsEditor
        open={lineItemsOpen}
        onOpenChange={setLineItemsOpen}
        initialItems={lineItems}
        onDone={(items) => {
          setLineItems(items);
          setLineItemsOpen(false);
        }}
      />
    </ListPage>
  );
}
